/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { get } from 'lodash';

import type { SportCategory } from 'app/normalizers/decodeCategory';
import Rows from 'app/components/layout/Rows';
import Icon from 'app/components/commons/Icon';
import RewriteLink from 'app/components/routing/RewriteLink';
import DropDownTab from './DropDownTab';
import LoadingTabs from './LoadingTabs';

type Props = {
  activeFilter?: string,
  activeSport: ?string,
  activeSubCategory: ?string,
  isLoading?: boolean,
  namespace: string,
  showAll?: boolean,
  showFeatured?: boolean,
  showSubCategories?: boolean,
  sportsList: Array<SportCategory>
};

type State = {
  content: ?('sport' | 'subCategory')
};

function getDisplayName(sport) {
  return sport.subcategories.length === 1 ? sport.subcategories[0].name : sport.name;
}

const borderStyle = '1px solid #333';

const Wrapper = styled.div({
  display: 'flex',
  borderBottom: borderStyle
});

const RowsWrapper = styled.div(({ theme, isOpen }) => ({
  backgroundColor: theme.color.basis1,
  position: 'absolute',
  width: '100vw',
  top: 0,
  left: 0,
  zIndex: theme.zIndex.overlay,
  opacity: isOpen ? 1 : 0,
  pointerEvents: !isOpen && 'none',
  transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
  transition: 'all 0.2s ease-out'
}));

const StyledLink = styled(RewriteLink)(
  {
    width: '100%',
    height: 52,
    textTransform: 'uppercase',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRight: borderStyle,
    borderBottom: borderStyle
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.small,
    backgroundColor: theme.color.basis1
  }),
  ({ disabled, theme }) =>
    disabled
      ? {
          pointerEvents: 'none',
          color: theme.color.basis3
        }
      : {
          color: theme.color.white
        }
);

const SportsIcon = styled(Icon)({
  width: 20,
  height: 20,
  flexShrink: 0,
  margin: 10
});

const Name = styled.div({
  flexGrow: 1,
  textAlign: 'left'
});

class MobileFilterBox extends React.Component<Props, State> {
  props: Props;

  state: State = {
    content: null
  };

  static defaultProps = {
    activeFilter: undefined,
    isLoading: false,
    showAll: false,
    showFeatured: false,
    showSubCategories: false
  };

  handleSportClick(sport: { subcategories: Array<Object> }) {
    if (sport.subcategories.length > 1) {
      this.setState(() => ({ content: 'subCategory' }));
    }
  }

  closeContent = () => {
    this.setState(() => ({ content: null }));
  };

  render() {
    const {
      activeFilter,
      activeSport,
      activeSubCategory,
      isLoading,
      namespace,
      showAll,
      showFeatured,
      showSubCategories,
      sportsList
    } = this.props;

    const { content } = this.state;

    if (isLoading) {
      return <LoadingTabs numTabs={2} height={50} />;
    }

    const selectedSport = sportsList.find(s => s.slug === activeSport) || null;
    const activeSubCategories = get(selectedSport, 'subcategories') || [];

    return (
      <div>
        <Wrapper>
          <DropDownTab
            icon={get(selectedSport, 'icon') || 'all'}
            name={selectedSport ? getDisplayName(selectedSport) : activeFilter || 'all'}
            isOpen={content === 'sport'}
            onClick={() =>
              this.setState(state => ({ content: state.content === 'sport' ? null : 'sport' }))
            }
          />
          {showSubCategories && (
            <DropDownTab
              disabled={!selectedSport || activeSubCategories.length <= 1}
              name={activeSubCategory || 'all'}
              isOpen={content === 'subCategory'}
              onClick={() =>
                this.setState(state => ({
                  content: state.content === 'subCategory' ? null : 'subCategory'
                }))
              }
            />
          )}
        </Wrapper>
        <div style={{ position: 'relative' }}>
          <RowsWrapper isOpen={content === 'sport'}>
            <Rows mobile={3} gutter={0}>
              {showAll && (
                <StyledLink to={namespace} onClick={this.closeContent}>
                  <SportsIcon type="icon-sport-all" />
                  <Name>All</Name>
                </StyledLink>
              )}
              {showFeatured && (
                <StyledLink to={namespace} onClick={this.closeContent}>
                  <SportsIcon type="icon-sport-all" />
                  <Name>Featured</Name>
                </StyledLink>
              )}
              {sportsList.map(sport => (
                <StyledLink
                  key={sport.name}
                  disabled={sport.disabled}
                  to={`${namespace}/${sport.slug}`}
                  onClick={this.closeContent}
                >
                  {sport.icon && <SportsIcon type={`icon-sport-${sport.icon}`} />}
                  <Name>{getDisplayName(sport)}</Name>
                </StyledLink>
              ))}
            </Rows>
          </RowsWrapper>
          {selectedSport && (
            <RowsWrapper isOpen={content === 'subCategory'}>
              <Rows mobile={4} gutter={0}>
                {showAll && (
                  <StyledLink to={`${namespace}/${get(selectedSport, 'slug')}`}>All</StyledLink>
                )}
                {activeSubCategories.map(subCategory => (
                  <StyledLink
                    key={subCategory.id}
                    disabled={subCategory.disabled}
                    to={`${namespace}/${selectedSport.slug}/${subCategory.slug}`}
                    onClick={this.closeContent}
                  >
                    {subCategory.name}
                  </StyledLink>
                ))}
              </Rows>
            </RowsWrapper>
          )}
        </div>
      </div>
    );
  }
}

export default MobileFilterBox;
